import { parseISO } from "date-fns";
import { z } from "zod";

export const AppointmentServiceSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  appointmentId: z.string(),
  serviceId: z.string(),
  price: z.number().nullable(),
  durationMinutes: z.number().nullable(),
  sequenceOrder: z.number().nullable(),
  notes: z.string().nullable(),
});

export const CreateAppointmentServiceRequestSchema =
  AppointmentServiceSchema.omit({
    id: true,
    createdAt: true,
    appointmentId: true,
  }).partial({
    price: true,
    durationMinutes: true,
    sequenceOrder: true,
    notes: true,
  });

export const UpdateAppointmentServiceRequestSchema =
  AppointmentServiceSchema.omit({
    id: true,
    createdAt: true,
    appointmentId: true,
    serviceId: true,
  }).partial();

export const AppointmentServiceDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  appointment_id: z.string(),
  service_id: z.string(),
  price: z.number().nullable(),
  duration_minutes: z.number().nullable(),
  sequence_order: z.number().nullable(),
  notes: z.string().nullable(),
});

export const CreateAppointmentServiceDtoSchema =
  AppointmentServiceDtoSchema.omit({
    id: true,
    created_at: true,
    appointment_id: true,
  }).partial({
    price: true,
    duration_minutes: true,
    sequence_order: true,
    notes: true,
  });

export const UpdateAppointmentServiceDtoSchema =
  AppointmentServiceDtoSchema.omit({
    id: true,
    created_at: true,
    appointment_id: true,
    service_id: true,
  }).partial();

// Infer DTO types
export type AppointmentServiceDto = z.infer<typeof AppointmentServiceDtoSchema>;
export type CreateAppointmentServiceDto = z.infer<
  typeof CreateAppointmentServiceDtoSchema
>;

// Infer domain types
export type AppointmentService = z.infer<typeof AppointmentServiceSchema>;
export type CreateAppointmentServiceRequest = z.infer<
  typeof CreateAppointmentServiceRequestSchema
>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromAppointmentServiceDtoToAppointmentService(
  dto: AppointmentServiceDto
): AppointmentService {
  const appointmentServiceDto = AppointmentServiceDtoSchema.parse(dto);
  return {
    id: appointmentServiceDto.id,
    createdAt: parseISO(appointmentServiceDto.created_at),
    appointmentId: appointmentServiceDto.appointment_id,
    serviceId: appointmentServiceDto.service_id,
    price: appointmentServiceDto.price,
    durationMinutes: appointmentServiceDto.duration_minutes,
    sequenceOrder: appointmentServiceDto.sequence_order,
    notes: appointmentServiceDto.notes,
  };
}

export function fromCreateAppointmentServiceRequestToCreateAppointmentServiceDto(
  request: CreateAppointmentServiceRequest
): CreateAppointmentServiceDto {
  const createAppointmentServiceRequest =
    CreateAppointmentServiceRequestSchema.parse(request);

  return {
    service_id: createAppointmentServiceRequest.serviceId,
    price: createAppointmentServiceRequest.price,
    duration_minutes: createAppointmentServiceRequest.durationMinutes,
    sequence_order: createAppointmentServiceRequest.sequenceOrder,
    notes: createAppointmentServiceRequest.notes,
  };
}
